import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import IconComponent from "../../cubeComponent/icons";
import { getUrlForImage } from "../../cubeComponent/constants";
import { useSelector } from "react-redux";
import { getImageUrl } from "@Utils";
import Text from "@CubeComponents/html/Text/Text";
import { RootState } from "@Reducers/index";
import { groupByBucket } from "./dateBuckets";
import PopperComponent from "@CubeComponents/html/PopperComponent";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";

function ImageWithFallback({ imageUrl, defaultImage }: any) {
  const [imgSrc, setImgSrc] = useState<any>(imageUrl);
  const handleError = () => {
    setImgSrc(defaultImage);
  };

  return (
    <img
      className="w-6 rounded-full mx-1 h-6 min-w-12 min-h-12"
      src={imgSrc}
      onError={handleError}
      alt="projectImage"
    />
  );
}

function NotificationItem({
  notification,
  layout,
  t,
  isHovered,
  isSelected,
  iconTypeMap,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onToggleSelect,
  onContextMenu,
}: any) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRef.current) {
      setOverflows(textRef.current.scrollHeight > textRef.current.clientHeight + 1);
    }
  }, [notification.text]);

  let cursorClass = "";

  if (isHovered) {
    cursorClass = notification.redirectUrl ? "cursor-pointer" : "cursor-not-allowed";
  }

  return (
    <div
      className={`px-4 py-2 rounded-[0.25rem] ${isSelected ? "bg-[#ECF3FE]" : ""}`}
      onContextMenu={(e) => onContextMenu?.(e, notification)}
    >
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(notification.id);
          }}
          className="rounded-full bg-blue-100 w-12 h-12 flex items-center justify-center relative cursor-pointer shrink-0 transition-transform duration-150 ease-out active:scale-90"
          title={isSelected ? (t("Deselect") as unknown as string) : (t("Select") as unknown as string)}
        >
          {isSelected ? (
            <CheckBoxComponent checked sx={{ p: 0, pointerEvents: "none" }} />
          ) : notification.type === "Project" ? (
            <ImageWithFallback
              imageUrl={getUrlForImage({ id: `${notification.projectId}` }, "projectImage")}
              defaultImage={`${getImageUrl() + layout.PROJECT_LOGO}`}
            />
          ) : (
            <IconComponent
              className="w-6 mx-1 h-6"
              name={iconTypeMap[notification.type] || "DefaultIcon"}
              width="1.5rem"
              height="1.5rem"
            />
          )}
        </button>
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <div className="flex items-start gap-2 w-full">
            <button
              onClick={onClick}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              className={`flex-1 min-w-0 text-start ${cursorClass}`}
              key={notification.id}
            >
              <div
                ref={textRef}
                className="text-sm"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: expanded ? "unset" : 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {notification.text}
              </div>
            </button>
            {(overflows || expanded) && (
              <ButtonComponent
                buttonType={BUTTON_TYPES.TEXT_BUTTON}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded((v) => !v);
                }}
                className="shrink-0 mt-0.5"
              >
                <IconComponent
                  name="arrowDownSm"
                  width="1rem"
                  height="1rem"
                  className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
                />
              </ButtonComponent>
            )}
          </div>
          <div className="flex">
            <Text className="text-xs">{notification.time}</Text>
          </div>
        </div>
      </div>
    </div>
  );
}

interface NotificationCardProps {
  notifications: any[];
  iconTypeMap: { [key: string]: string };
  dismissNotification: (id: string) => void;
  toggleReadState: (id: string, currentRead: string) => void;
  closeNotificationModal: () => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
}

function NotificationCard({
  notifications,
  iconTypeMap,
  dismissNotification,
  toggleReadState,
  closeNotificationModal,
  selectedIds,
  onToggleSelect,
}: NotificationCardProps) {
  const { t } = useTranslation();
  const layout = useSelector((state: RootState) => state.layout);
  const navigate = useNavigate();

  const [hoveredNotification, setHoveredNotification] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ anchorEl: any; notification: any } | null>(null);

  const closeContextMenu = () => setContextMenu(null);

  const handleContextMenu = (e: React.MouseEvent, notification: any) => {
    e.preventDefault();
    const { clientX, clientY } = e;
    setContextMenu({
      anchorEl: { getBoundingClientRect: () => new DOMRect(clientX, clientY, 0, 0) },
      notification,
    });
  };

  const handleToggleReadFromMenu = () => {
    if (!contextMenu) return;
    toggleReadState(contextMenu.notification.id, contextMenu.notification.read);
    closeContextMenu();
  };

  const bucketedGroups = useMemo(() => groupByBucket(notifications, t), [notifications, t]);

  const handleMouseEnter = (notification: any) => {
    setHoveredNotification(notification.id);
  };

  const handleMouseLeave = () => {
    setHoveredNotification(null);
  };

  const handleRedirect = (redirectUrl: any) => {
    navigate(redirectUrl);
    closeNotificationModal();
  };

  const notificationClicked = (notification: any) => {
    if (contextMenu) return;
    if (notification.redirectUrl) {
      if (notification.read === "NOTIFICATION_UNREAD") {
        dismissNotification(notification.id);
      }
      handleRedirect(notification.redirectUrl);
    }
  };

  const handleDismiss = (e: any, notificationId: any) => {
    e.stopPropagation();
    dismissNotification(notificationId);
  };

  return (
    <div className="w-full">
      {bucketedGroups.map(({ bucket, items }) => (
        <div key={bucket.key} className="flex flex-col">
          <div className="text-black font-semibold flex px-8 text-base py-3 bg-white sticky top-0 z-[1]">
            <Text>{bucket.label}</Text>
          </div>
          <div className="flex w-full flex-col gap-2 px-4">
            {items.map((notification: any) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                layout={layout}
                t={t}
                iconTypeMap={iconTypeMap}
                isHovered={hoveredNotification === notification.id}
                isSelected={selectedIds.has(notification.id)}
                onMouseEnter={() => handleMouseEnter(notification)}
                onMouseLeave={handleMouseLeave}
                onClick={() => notificationClicked(notification)}
                onDismiss={(e: any) => handleDismiss(e, notification.id)}
                onToggleSelect={onToggleSelect}
                onContextMenu={handleContextMenu}
              />
            ))}
          </div>
        </div>
      ))}
      <PopperComponent
        open={!!contextMenu}
        anchorEl={contextMenu?.anchorEl}
        onClose={closeContextMenu}
        placement="bottom-start"
        paperProps={{ className: "preventNotificationClose", onMouseLeave: undefined }}
      >
        <ButtonComponent
          buttonType={BUTTON_TYPES.TEXT_BUTTON}
          fullWidth
          type="button"
          onClick={handleToggleReadFromMenu}
          className="text-start"
        >
          {contextMenu?.notification.read === "NOTIFICATION_UNREAD" ? t("Mark as read") : t("Mark as unread")}
        </ButtonComponent>
      </PopperComponent>
    </div>
  );
}

export default NotificationCard;
